import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Mail, Phone, MapPin, Send, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function ContactSection({ texts }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...formData })
      });
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      console.error('Form submission error:', err);
      alert('There was an error submitting the form. Please try again.');
    }
  };

  const contactInfo = [
    { icon: Mail, emoji: "📧", ...texts.contactInfo[0], color: "from-blue-500 to-cyan-500" },
    { icon: Phone, emoji: "📞", ...texts.contactInfo[1], color: "from-green-500 to-teal-500" },
    { icon: MapPin, emoji: "📍", ...texts.contactInfo[2], color: "from-purple-500 to-pink-500" }
  ];

  return (
    <section id="contact" className="py-24 bg-black relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="bg-gray-800 text-green-400 border-green-500/20 mb-4">
            {texts.badge}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {texts.title1}
            </span>
            <br />
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              {texts.title2}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {texts.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-14">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-colors duration-300 rounded-2xl">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-8">{texts.formTitle}</h3>
                
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-white mb-2">{texts.formSuccess}</h4>
                    <p className="text-gray-400">{texts.formSuccessDesc}</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} name="contact" method="POST" data-netlify="true" className="space-y-7">
                    <input type="hidden" name="form-name" value="contact" />
                    <div className="grid md:grid-cols-2 gap-7">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">{texts.formName}</label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                          placeholder={texts.formName}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">{texts.formCompany}</label>
                        <Input
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                          placeholder={texts.formCompany}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{texts.formEmail}</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20 h-12"
                        placeholder="email@company.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{texts.formMessage}</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20 min-h-[160px]"
                        placeholder="..."
                        required
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                    >
                      {texts.formSubmit}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">{texts.infoTitle}</h3>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {texts.infoDesc}
              </p>
            </div>

            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-4 p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-gray-700 transition-colors duration-300"
                >
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${info.color} bg-opacity-10 border border-gray-700`}>
                    <span className="text-2xl">{info.emoji}</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{info.label}</p>
                    <p className="text-white font-medium">{info.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>

                  <div className="mt-10 p-7 rounded-xl bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-500/20 hover:border-blue-400/30 transition-colors duration-300">
              <h4 className="text-lg font-semibold text-white mb-2">{texts.ctaTitle}</h4>
              <p className="text-gray-400 text-sm">
                {texts.ctaDesc}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
